import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback only -- used before /api/config/public has loaded, or if the fetch
// fails. The canonical, single source of truth is backend/intake.py
// (INCIDENT_CATEGORIES + INCIDENT_CATEGORY_LABELS), served via that endpoint's
// `incident_categories` field. This is the ONE place a static copy lives on
// the frontend; pages must consume `config.incident_categories` from
// usePublicConfig() rather than hardcoding their own list, so the three
// previously-independent copies (backend, RequesterDashboard, CallsConsole)
// can't drift out of sync again.
export const INCIDENT_CATEGORY_FALLBACK = [
  { value: 'UNWARRANTED POLICE STOPS / HARASSMENT', label: 'Police harassment or stop' },
  { value: 'ROAD TRAFFIC DISOBEDIENCE', label: 'Traffic arrest or violation' },
  { value: 'EXPIRED / INVALID VEHICLE DOCUMENT', label: 'Vehicle document issue' },
  { value: 'ILLICIT DRUGS POSSESSION', label: 'Drug/package allegation' },
  { value: 'ARMED ROBBERY', label: 'Armed robbery' },
  { value: 'THEFT / ROBBERY', label: 'Theft or robbery' },
  { value: 'PHYSICAL ALTERCATION', label: 'Physical assault' },
  { value: 'MOB ATTACK / THEFT', label: 'Mob attack or group theft' },
  { value: 'VEHICLE ACCIDENT', label: 'Vehicle accident' },
  { value: 'MEDICAL EMERGENCY / INJURY', label: 'Medical emergency' },
  { value: 'SEXUAL ASSAULT', label: 'Sexual assault' },
  { value: 'UBER APP ISSUES', label: 'Uber app issue' },
  { value: 'COMMERCIAL DISAGREEMENTS', label: 'Fare or payment dispute' },
  { value: 'VEHICLE BREAKDOWN / CRASH', label: 'Vehicle breakdown' },
  { value: 'MISSING DRIVER / PASSENGER', label: 'Missing driver or passenger' },
  { value: 'NORMAL DISAGREEMENT', label: 'Non-physical disagreement' },
  { value: 'OTHER ISSUES', label: 'Other safety issue' },
];

export const DEFAULT_PUBLIC_CONFIG = {
  hotline_number: '',
  storage_provider: 'local',
  max_upload_bytes: 25 * 1024 * 1024,
  allowed_upload_types: [],
  web_push_enabled: false,
  web_push_send_configured: false,
  web_push_public_key: '',
  incident_categories: INCIDENT_CATEGORY_FALLBACK,
};

export async function fetchPublicConfig(token) {
  const response = await fetch(`${API_URL}/api/config/public`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error('Unable to load public config');
  return response.json();
}

export function usePublicConfig(token) {
  const [config, setConfig] = useState(DEFAULT_PUBLIC_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let canceled = false;

    async function loadConfig() {
      try {
        setLoading(true);
        const data = await fetchPublicConfig(token);
        if (canceled) return;
        setConfig(prev => ({ ...prev, ...data }));
        setError(null);
      } catch (loadError) {
        if (!canceled) {
          setError(loadError.message || 'Unable to load public config');
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    }

    loadConfig();
    return () => {
      canceled = true;
    };
  }, [token]);

  return { config, loading, error };
}
